import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  FileCode,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Copy,
  X,
  Loader2,
  Eye,
  Code,
  HelpCircle,
} from 'lucide-react';
import { Button } from '../Button';
import { Modal } from '../ui/Modal';
import {
  parseMarkdownToForm,
  getExampleMarkdown,
  type ParseResult,
} from '../../lib/markdown/markdownParser';
import { FIELD_ICONS, FIELD_ICON_FALLBACK } from '../../lib/fieldIcons';
import type { FormField } from '../../types/forms';

interface MarkdownImporterProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (result: ParseResult, mode: 'replace' | 'append') => void;
  hasExistingFields: boolean;
}

export const MarkdownImporter: React.FC<MarkdownImporterProps> = ({
  isOpen,
  onClose,
  onImport,
  hasExistingFields,
}) => {
  const [markdown, setMarkdown] = useState('');
  const [showPreview, setShowPreview] = useState(true);
  const [isParsing, setIsParsing] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [importMode, setImportMode] = useState<'replace' | 'append'>('replace');

  // Parsear markdown com debounce
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);

  // Debounce do parsing
  useEffect(() => {
    if (!markdown.trim()) {
      setParseResult(null);
      return;
    }

    setIsParsing(true);
    const timeoutId = setTimeout(() => {
      try {
        const result = parseMarkdownToForm(markdown);
        setParseResult(result);
      } catch (err) {
        console.error('Parse error:', err);
        setParseResult({
          success: false,
          title: '',
          description: '',
          settings: {},
          fields: [],
          errors: [{ line: 0, message: 'Erro inesperado ao parsear o Markdown', severity: 'error' }],
          warnings: [],
        });
      } finally {
        setIsParsing(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [markdown]);

  // Carregar exemplo
  const handleLoadExample = useCallback(() => {
    setMarkdown(getExampleMarkdown());
  }, []);

  // Copiar exemplo
  const handleCopyExample = useCallback(() => {
    navigator.clipboard.writeText(getExampleMarkdown());
  }, []);

  // Limpar
  const handleClear = useCallback(() => {
    setMarkdown('');
    setParseResult(null);
  }, []);

  // Importar
  const handleImport = useCallback(() => {
    if (!parseResult || !parseResult.success) return;
    onImport(parseResult, importMode);
    onClose();
    setMarkdown('');
    setParseResult(null);
  }, [parseResult, importMode, onImport, onClose]);

  // Fechar e limpar
  const handleClose = useCallback(() => {
    onClose();
    // Manter o conteúdo por alguns segundos caso o usuário feche sem querer
  }, [onClose]);

  // Estatísticas
  const stats = useMemo(() => {
    if (!parseResult) return null;
    return {
      fields: parseResult.fields.length,
      required: parseResult.fields.filter(f => f.required).length,
      sections: new Set(parseResult.fields.map(f => f.block).filter(Boolean)).size,
    };
  }, [parseResult]);

  // Verificar se pode importar
  const canImport = parseResult?.success && parseResult.fields.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Importar de Markdown"
      size="xl"
    >
      <div className="flex flex-col h-[78dvh]">
        {/* Toolbar */}
        <div className="flex items-center justify-between gap-4 pb-4 border-b border-neutral-700/50">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleLoadExample}
              className="text-sm"
            >
              <FileCode className="w-4 h-4 mr-2" />
              Carregar exemplo
            </Button>
            <Button
              variant="outline"
              onClick={handleCopyExample}
              className="text-sm"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copiar exemplo
            </Button>
            {markdown && (
              <Button
                variant="outline"
                onClick={handleClear}
                className="text-sm text-neutral-400"
              >
                <X className="w-4 h-4 mr-2" />
                Limpar
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowHelp(!showHelp)}
              className={`p-2 rounded-lg transition-colors ${
                showHelp ? 'bg-primary-500/20 text-primary-400' : 'text-neutral-400 hover:text-white'
              }`}
              title="Ajuda"
            >
              <HelpCircle className="w-5 h-5" />
            </button>
            <div className="flex rounded-lg overflow-hidden border border-neutral-700">
              <button
                onClick={() => setShowPreview(false)}
                className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition-colors ${
                  !showPreview ? 'bg-neutral-700 text-white' : 'text-neutral-400 hover:text-white'
                }`}
              >
                <Code className="w-4 h-4" />
                Código
              </button>
              <button
                onClick={() => setShowPreview(true)}
                className={`px-3 py-1.5 text-sm flex items-center gap-1.5 transition-colors ${
                  showPreview ? 'bg-neutral-700 text-white' : 'text-neutral-400 hover:text-white'
                }`}
              >
                <Eye className="w-4 h-4" />
                Preview
              </button>
            </div>
          </div>
        </div>

        {/* Ajuda */}
        {showHelp && (
          <div className="mt-4 p-4 rounded-xl bg-primary-500/10 border border-primary-500/30">
            <h4 className="text-sm font-semibold text-primary-400 mb-2">Como usar</h4>
            <div className="text-sm text-neutral-300 space-y-1">
              <p><code className="bg-neutral-800 px-1 rounded">---</code> Delimitadores do frontmatter (título, descrição, settings)</p>
              <p><code className="bg-neutral-800 px-1 rounded"># Seção: Nome</code> Cria uma seção para agrupar campos</p>
              <p><code className="bg-neutral-800 px-1 rounded">## Label do campo *</code> Cria um campo (* = obrigatório)</p>
              <p><code className="bg-neutral-800 px-1 rounded">- type: text</code> Define o tipo (text, email, select, checkbox...)</p>
              <p><code className="bg-neutral-800 px-1 rounded">- options: A, B, C</code> Opções para select/checkbox/radio</p>
              <p><code className="bg-neutral-800 px-1 rounded">- placeholder: Texto</code> Placeholder do campo</p>
              <p><code className="bg-neutral-800 px-1 rounded">- help: Texto</code> Texto de ajuda</p>
              <p className="pt-2 mt-2 border-t border-primary-500/20 text-neutral-400 text-xs">
                Para exemplo completo com os 25 tipos disponíveis (Welcome, ThankYou, NPS, Rating, Picture Choice, etc.), clique em <span className="text-primary-400 font-medium">Carregar exemplo</span> acima.
              </p>
            </div>
          </div>
        )}

        {/* Conteúdo principal */}
        <div className="flex-1 flex gap-4 mt-4 min-h-0">
          {/* Editor */}
          <div className={`flex flex-col ${showPreview ? 'w-1/2' : 'w-full'}`}>
            <label className="text-sm text-neutral-400 mb-2">Cole seu Markdown aqui:</label>
            <textarea
              value={markdown}
              onChange={(e) => setMarkdown(e.target.value)}
              placeholder={`---
title: Nome do Formulário
description: Descrição opcional
---

# Seção: Identificação

## Nome completo *
- type: text
- placeholder: Digite seu nome

## Email *
- type: email`}
              className="flex-1 w-full bg-neutral-900/50 border border-neutral-700 rounded-xl p-4 text-neutral-50 placeholder-neutral-600 font-mono text-sm resize-none focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20"
              spellCheck={false}
            />

            {/* Status */}
            <div className="mt-2 flex items-center gap-4 text-sm">
              {isParsing && (
                <span className="flex items-center gap-2 text-neutral-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Analisando...
                </span>
              )}
              {!isParsing && parseResult && (
                <>
                  {parseResult.success ? (
                    <span className="flex items-center gap-2 text-green-400">
                      <CheckCircle className="w-4 h-4" />
                      Válido
                    </span>
                  ) : (
                    <span className="flex items-center gap-2 text-red-400">
                      <AlertCircle className="w-4 h-4" />
                      {parseResult.errors.length} erro(s)
                    </span>
                  )}
                  {parseResult.warnings.length > 0 && (
                    <span className="flex items-center gap-2 text-yellow-400">
                      <AlertTriangle className="w-4 h-4" />
                      {parseResult.warnings.length} aviso(s)
                    </span>
                  )}
                  {stats && (
                    <span className="text-neutral-500">
                      {stats.fields} campo(s) • {stats.required} obrigatório(s)
                      {stats.sections > 0 && ` • ${stats.sections} seção(ões)`}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Preview */}
          {showPreview && (
            <div className="w-1/2 flex flex-col min-h-0">
              <label className="text-sm text-neutral-400 mb-2">Preview dos campos:</label>
              <div className="flex-1 bg-neutral-900/30 border border-neutral-700 rounded-xl p-4 overflow-y-auto">
                {!parseResult && !isParsing && (
                  <div className="flex flex-col items-center justify-center h-full text-neutral-500">
                    <FileCode className="w-12 h-12 mb-2 opacity-50" />
                    <p>Cole o Markdown para ver o preview</p>
                  </div>
                )}

                {isParsing && (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
                  </div>
                )}

                {parseResult && !isParsing && (
                  <div className="space-y-4">
                    {/* Erros */}
                    {parseResult.errors.length > 0 && (
                      <div className="space-y-2">
                        {parseResult.errors.map((error, i) => (
                          <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                            <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm text-red-400">{error.message}</p>
                              {error.line > 0 && (
                                <p className="text-xs text-red-400/60">Linha {error.line}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Warnings */}
                    {parseResult.warnings.length > 0 && (
                      <div className="space-y-2">
                        {parseResult.warnings.map((warning, i) => (
                          <div key={i} className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
                            <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm text-yellow-400">{warning.message}</p>
                              {warning.line > 0 && (
                                <p className="text-xs text-yellow-400/60">Linha {warning.line}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Título e descrição */}
                    {parseResult.title && (
                      <div className="pb-3 border-b border-neutral-700/50">
                        <h3 className="text-lg font-semibold text-white">{parseResult.title}</h3>
                        {parseResult.description && (
                          <p className="text-sm text-neutral-400 mt-1">{parseResult.description}</p>
                        )}
                      </div>
                    )}

                    {/* Campos */}
                    {parseResult.fields.length > 0 && (
                      <div className="space-y-2">
                        {parseResult.fields.map((field, i) => {
                          const Icon = FIELD_ICONS[field.type] || FIELD_ICON_FALLBACK;
                          const prevField = parseResult.fields[i - 1];
                          const showSectionHeader = field.block && field.block !== prevField?.block;

                          return (
                            <React.Fragment key={field.id}>
                              {showSectionHeader && (
                                <div className="pt-3 pb-1">
                                  <p className="text-xs font-semibold text-primary-400 uppercase tracking-wide">
                                    {field.block}
                                  </p>
                                </div>
                              )}
                              <div className="flex items-center gap-3 p-3 rounded-lg bg-neutral-800/50 border border-neutral-700/50">
                                <div className="w-8 h-8 rounded-lg bg-neutral-700/50 flex items-center justify-center flex-shrink-0">
                                  <Icon className="w-4 h-4 text-neutral-400" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-white truncate">
                                    {field.label}
                                    {field.required && <span className="text-red-400 ml-1">*</span>}
                                  </p>
                                  <p className="text-xs text-neutral-500">
                                    {field.type}
                                    {field.options && ` • ${field.options.length} opções`}
                                  </p>
                                </div>
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    )}

                    {parseResult.fields.length === 0 && parseResult.errors.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-8 text-neutral-500">
                        <AlertTriangle className="w-8 h-8 mb-2 opacity-50" />
                        <p>Nenhum campo encontrado</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-4 pt-4 mt-4 border-t border-neutral-700/50">
          <div>
            {hasExistingFields && canImport && (
              <div className="flex items-center gap-4">
                <span className="text-sm text-neutral-400">Modo de importação:</span>
                <div className="flex rounded-lg overflow-hidden border border-neutral-700">
                  <button
                    onClick={() => setImportMode('replace')}
                    className={`px-3 py-1.5 text-sm transition-colors ${
                      importMode === 'replace' ? 'bg-neutral-700 text-white' : 'text-neutral-400 hover:text-white'
                    }`}
                  >
                    Substituir campos
                  </button>
                  <button
                    onClick={() => setImportMode('append')}
                    className={`px-3 py-1.5 text-sm transition-colors ${
                      importMode === 'append' ? 'bg-neutral-700 text-white' : 'text-neutral-400 hover:text-white'
                    }`}
                  >
                    Adicionar ao final
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleImport}
              disabled={!canImport}
            >
              <FileCode className="w-4 h-4 mr-2" />
              Importar {stats ? `${stats.fields} campo(s)` : ''}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
